import { ApiResponse } from "@/types";
import {
  AllSystemSetting,
  EmailTemplate,
  EmailTemplateTable,
  EmailTrigger,
  EmailTriggerTable,
  InvoiceReminders,
  SystemSettings,
} from "@/types/system-setting/system-config";
import { apiSlice } from "@/redux/apiSlice";

export const SystemConfig = apiSlice.injectEndpoints({
  endpoints(builder) {
    return {
      getRefundValues: builder.query<{ value: number; label: string }[], void>({
        query: () => ({
          url: `/settings?groups=refund`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["SysConfig","Organization"], 
        transformResponse: (response: any) => {
          const periods = response?.refund?.cancellation_period ?? [];
          return periods.map((label: string, index: number) => ({
            value: label,
            label,
          }));
        },
      }),
      getSystemSettings: builder.query<AllSystemSetting, { query: string }>({
        query: ({ query }) => ({
          url: `/settings?${query}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["SysConfig","Memberships","Invoice","Transaction","Organization"],
      }),
      updateSystemSettings: builder.mutation<ApiResponse, AllSystemSetting>({
        query: (data) => ({
          url: `/settings/groups`,
          method: "PUT",
          body: data,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["SysConfig","Memberships","Invoice","Transaction","Organization"],
      }),
      createEmailTrigger: builder.mutation<ApiResponse, EmailTrigger>({
        query: (data) => ({
          url: `/settings/email-triggers`,
          method: "POST",
          body: data,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["SysConfig","Organization"],
      }),
      updateEmailTrigger: builder.mutation<ApiResponse, EmailTrigger>({
        query: (data) => ({
          url: `/settings/email-triggers/${data.id}`,
          method: "PUT",
          body: data,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["SysConfig","Organization"],
      }),
      deleteEmailTrigger: builder.mutation<ApiResponse, EmailTrigger>({
        query: (data) => ({
          url: `/settings/email-triggers/${data.id}`,
          method: "DELETE",
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["SysConfig","Organization"],
      }),
      getEmailTriggers: builder.query<EmailTriggerTable, { query: string }>({
        query: ({ query }) => ({
          url: `/settings/email-triggers?${query}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["SysConfig","Organization"],
      }),
      getEmailTriggerById: builder.query<EmailTrigger, number>({
        query: (id) => ({
          url: `/settings/email-triggers/${id}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["SysConfig","Organization"],
      }),
      getInvoiceReminders: builder.query<InvoiceReminders, void>({
        query: () => ({
          url: `/settings/invoice-reminders`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["SysConfig","Organization"],
      }),
      updateInvoiceReminders: builder.mutation<ApiResponse, InvoiceReminders>({
        query: (data) => ({
          url: `/settings/invoice-reminders`,
          method: "PUT",
          body: data,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["SysConfig","Organization"],
      }),
      testEmail: builder.mutation<
        ApiResponse,
        { name?: string; subject?: string; body?: string }
      >({
        query: (data) => ({
          url: `/settings/test-email-template`,
          method: "POST",
          body: data,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["SysConfig","Organization"],
      }),
      getEmailTemplates: builder.query<EmailTemplateTable, { query: string }>({
        query: ({ query }) => ({
          url: `/settings/email-templates${
            query.length > 0 ? `?${query}` : ""
          }`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["SysConfig","Organization"],
      }),
      createEmailTemplates: builder.mutation<ApiResponse, EmailTemplate>({
        query: (data) => ({
          url: `/settings/email-templates`,
          method: "POST",
          body: data,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["SysConfig","Organization"],
      }),
      updateEmailTemplates: builder.mutation<ApiResponse, EmailTemplate>({
        query: (data) => ({
          url: `/settings/email-templates/${data.id}`,
          method: "PUT",
          body: data,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["SysConfig","Organization"],
      }),
      deleteEmailTemplates: builder.mutation<ApiResponse, number>({
        query: (id) => ({
          url: `/settings/email-templates/${id}`,
          method: "DELETE",
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["SysConfig","Organization"],
      }),
    };
  },
});

export const {
  useGetRefundValuesQuery,
  useGetSystemSettingsQuery,
  useUpdateSystemSettingsMutation,
  useCreateEmailTriggerMutation,
  useUpdateEmailTriggerMutation,
  useDeleteEmailTriggerMutation,
  useGetEmailTriggersQuery,
  useGetEmailTriggerByIdQuery,
  useGetInvoiceRemindersQuery,
  useUpdateInvoiceRemindersMutation,
  useTestEmailMutation,
  useGetEmailTemplatesQuery,
  useCreateEmailTemplatesMutation,
  useUpdateEmailTemplatesMutation,
  useDeleteEmailTemplatesMutation,
} = SystemConfig;
